import { useState } from 'react';
import { useAuth } from '@edusense/shared-auth-core';
// 🚀 GraphQL Mutation 사용 (feed-client로 완전 통일)
import {
  useCreateTweetWithMedia,
  useLikeTweet,
  useUnlikeTweet,
} from '@edusense/feed-client';
import { useTweetStore } from './tweet/useTweetStore';
import type { FeedItem } from './useFeedData';

/**
 * 트윗 관련 액션들을 관리하는 커스텀 훅
 * @param navigate - 네비게이션 함수 (Router Context 의존성 제거를 위해 props로 전달)
 */
export const useTweetActions = (navigate: (path: string) => void) => {
  const { isAuthenticated } = useAuth();
  const tweetStore = useTweetStore(null);
  
  const [replyToTweet, setReplyToTweet] = useState<FeedItem | null>(null);
  const [showComposeModal, setShowComposeModal] = useState(false);

  // React Query 훅들 사용
  const likeTweetMutation = useLikeTweet();
  const unlikeTweetMutation = useUnlikeTweet();
  const createTweetMutation = useCreateTweetWithMedia();

  // 좋아요 핸들러
  const handleLike = async (id: string, isLiked: boolean) => {
    if (!isAuthenticated) {
      navigate('/home');
      return;
    }

    try {
      // React Query mutation 사용으로 좋아요/좋아요 취소 처리
      if (isLiked) {
        await likeTweetMutation.mutateAsync({ id: parseInt(id) });
      } else {
        await unlikeTweetMutation.mutateAsync({ id: parseInt(id) });
      }
      console.log('Like action completed:', { id, isLiked });

      // 피드 데이터 업데이트 (옵티미스틱 업데이트)
      const updatedMedia = tweetStore.media.map((item: FeedItem) =>
        item.id === id
          ? {
            ...item,
            likesCount: isLiked ? item.likesCount + 1 : Math.max(0, item.likesCount - 1),
            isLiked: isLiked
          }
          : item
      );
      tweetStore.setMedia(updatedMedia);
    } catch (error) {
      console.error('좋아요 처리 중 오류 발생:', error);
    }
  };

  // 댓글 핸들러
  const handleComment = (id: string) => {
    console.log(`Comment on post ${id}`);

    if (!isAuthenticated) {
      navigate('/home');
      return;
    }

    // 답글을 달 트윗 찾기
    const targetTweet = tweetStore.media.find((item: FeedItem) => item.id === id);
    if (targetTweet) {
      setReplyToTweet(targetTweet);
      setShowComposeModal(true);
    }
  };

  // 트윗 제출 핸들러
  const handleTweetSubmit = async (data: any) => {
    try {
      console.log('트윗 제출 데이터:', data);
      // React Query mutation 사용으로 트윗 생성
      await createTweetMutation.mutateAsync({ data });

      // 성공 시 모달 닫기
      setShowComposeModal(false);

      // 피드 새로고침은 부모 컴포넌트에서 처리
      return true;
    } catch (error) {
      console.error('트윗 제출 중 오류 발생:', error);
      return false;
    }
  };

  // 모달 닫기 핸들러
  const handleCloseModal = () => {
    setShowComposeModal(false);
    setReplyToTweet(null);
  };

  return {
    handleLike,
    handleComment,
    handleTweetSubmit,
    handleCloseModal,
    replyToTweet,
    showComposeModal
  };
};